import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReusablePane from './reusablePane';
import { Modal, Button, Tab } from 'semantic-ui-react';
import { isMobileOnly } from 'mobile-device-detect';
import { useTextResources } from '../../language';
import { onError, onSuccess } from '../shared/messages';
import { useLoader } from '../shared/utils';
import { ReservationPolicy } from '../../api/services/next';
import { propertiesEnum, sourceEnum } from './enums';
import uniqid from 'uniqid';
import './style.css';

const {
  AddAndUpdateCancellationAndDepositPolicies,
  DeleteCancellationPolicy,
  DeleteDepositPolicy,
} = ReservationPolicy;

const CancellationAndDepositPoliciesPopup = ({
  reservationPolicy,
  onClose,
  startOpen,
  ...rest
}) => {
  const [open, setOpen] = useState(startOpen);
  const [depositsCopy, setDepositsCopy] = useState(
    reservationPolicy.DepositPolicies?.map((d) => ({ ...d })) //Detach from the reference
  );
  const [cancellationsCopy, setCancellationsCopy] = useState(
    reservationPolicy.CancellationPolicies?.map((c) => ({ ...c })) //Detach from the reference
  );
  const [removedCancellations, setRemovedCancellations] = useState([]);
  const [removedDeposits, setRemovedDeposits] = useState([]);
  const [timeReference, setTimeReference] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [depositsErrors, setDepositsErrors] = useState();
  const [cancellationsErrors, setCancellationsErrors] = useState();

  const text = useTextResources();
  const loader = useLoader();

  useEffect(() => {
    setOpen(startOpen);
  }, [startOpen]);

  const close = (props) => {
    setOpen(false);
    onClose?.(props);
    clearScreenData();
  };

  const clearScreenData = () => {
    setHasChanges(false);
    resetErrors();
    setDepositsCopy([]);
    setCancellationsCopy([]);
  };

  const resetErrors = () => {
    setCancellationsErrors({});
    setDepositsErrors({});
  };

  const save = async () => {
    //   const isValid = isReadyForSave()
    //   if(!isValid) return;

    try {
      if (!hasChanges || !checkIsValidForm()) return;

      loader(true);

      if (removedDeposits.length || removedCancellations.length) {
        await Promise.all([
          ...removedDeposits?.map((d) => DeleteDepositPolicy(d.Id)),
          ...removedCancellations?.map((d) => DeleteCancellationPolicy(d.Id)),
        ]);
      }

      const { CancellationPolicies, DepositPolicies } =
        await AddAndUpdateCancellationAndDepositPolicies({
          ...reservationPolicy,
          CancellationPolicies: cancellationsCopy.filter((c) => c.IsDirty),
          DepositPolicies: depositsCopy.filter((d) => d.IsDirty),
        });

      onSuccess();
      loader(false);
      close({
        cancellations: CancellationPolicies,
        deposits: DepositPolicies,
        reservationPolicyId: reservationPolicy.Id,
      });
    } catch (e) {
      onError(e);
      console.error(e);
    } finally {
      loader(false);
    }
  };

  const onTimeReferenceChange = useCallback(
    ({ value, policiesArray, setStateFunction }) => {
      setHasChanges(true);
      setTimeReference(value);
      setStateFunction(
        policiesArray.map((p) => ({ ...p, TimeReference: value }))
      );
    },
    []
  );

  const onUpdatePolicy = useCallback(
    ({ value, policy, policiesArray, property, setStateFunction }) => {
      policy[property] = value;
      policy.IsDirty = true;
      policy.TimeReference = timeReference;
      setStateFunction([...policiesArray]);
      setHasChanges(true);
      resetErrors();
    },
    [timeReference]
  );

  const onAddNewPolicy = useCallback(
    (setStateFunction) => {
      const newPolicy = {
        Id: 0,
        InternalId: uniqid('newPolicy-'),
        TimeReference: timeReference,
        ReservationPolicyId: reservationPolicy.Id,
        Days: 0,
        Percent: null,
        Amount: null,
        CurrencyId: null,
        IsDirty: true,
      };
      //   policiesArray?.push(newPolicy);
      //   setStateFunction(policiesArray);
      setStateFunction((prev) => [...prev, newPolicy]);
      setHasChanges(true);
    },
    [reservationPolicy.Id, timeReference]
  );

  const onDeletePolicy = useCallback(
    async ({ policiesArray, policy, setStateFunction }) => {
      setHasChanges(true);
      setStateFunction((prev) =>
        prev.filter(
          (p) => p.InternalId != policy.InternalId || p.Id != policy.Id
        )
      );
      if (policy.Id) {
        policiesArray == depositsCopy
          ? setRemovedDeposits((prev) => [...prev, policy])
          : setRemovedCancellations((prev) => [...prev, policy]);
      }
    },
    [depositsCopy]
  );

  const currencyValid = useCallback((policy) => {
    if (!policy.CurrencyId && policy.Amount) return false;
    return true;
  }, []);

  const amountValid = useCallback((policy) => {
    if (!policy.Amount && policy.CurrencyId) return false;
    return true;
  }, []);

  const depositsEitherAmountOrPercentsValid = useCallback((policy) => {
    if (policy.Amount && policy.Percent) return false;
    return true;
  }, []);

  const daysFieldValid = useCallback((policy) => {
    if (!policy.Days) return false;
    else return true;
  }, []);

  const checkIsValidForm = () => {
    let shouldSave = true;

    const setErrorAndMarkShouldNotSave = (policy, policiesArray, setErrors) => {
      const errorSetter = ({ property, text }) => {
        setErrors((prev) => ({
          ...prev,
          [property]: {
            ...prev[property],
            [policy.Id || policy.InternalId]: text,
          },
        }));
      };

      if (!currencyValid(policy)) {
        errorSetter({
          text: text('CurrencyMustAccompanyAmount'),
          property: propertiesEnum.Currency,
        });
        shouldSave = false;
      }

      if (!amountValid(policy)) {
        errorSetter({
          text: text('AmountMustAccompanyCurrency'),
          property: propertiesEnum.Amount,
        });
        shouldSave = false;
      }

      if (!daysFieldValid(policy)) {
        errorSetter({
          text: text('PleaseChooseNumberOfDays'),
          property: propertiesEnum.Days,
        });
        shouldSave = false;
      }

      if (policiesArray == depositsCopy) {
        if (!depositsEitherAmountOrPercentsValid(policy)) {
          errorSetter({
            text: text('EitherAmountOrPercents'),
            property: propertiesEnum.AmountOrPercentInDeposits,
          });
          shouldSave = false;
        }
      }
    };

    depositsCopy.forEach((deposit) =>
      setErrorAndMarkShouldNotSave(deposit, depositsCopy, setDepositsErrors)
    );

    cancellationsCopy.forEach((cancellation) =>
      setErrorAndMarkShouldNotSave(
        cancellation,
        cancellationsCopy,
        setCancellationsErrors
      )
    );

    return shouldSave;
  };

  const valuesAndHandlersPackage = useMemo(
    () => ({
      timeReference,
      onTimeReferenceChange,
      onUpdatePolicy,
      onDeletePolicy,
      onAddNewPolicy,
    }),
    [
      onTimeReferenceChange,
      onAddNewPolicy,
      onDeletePolicy,
      onUpdatePolicy,
      timeReference,
    ]
  );

  const depositsPane = useCallback(
    () => (
      <ReusablePane
        key='depositsPane'
        policiesArray={depositsCopy}
        setStateFunction={setDepositsCopy}
        source={sourceEnum.deposits}
        errors={depositsErrors}
        {...valuesAndHandlersPackage}
      />
    ),
    [depositsCopy, depositsErrors, valuesAndHandlersPackage]
  );

  const cancellationsPane = useCallback(
    () => (
      <ReusablePane
        key='cancellationsPane'
        policiesArray={cancellationsCopy}
        setStateFunction={setCancellationsCopy}
        source={sourceEnum.cancellations}
        errors={cancellationsErrors}
        {...valuesAndHandlersPackage}
      />
    ),
    [cancellationsCopy, cancellationsErrors, valuesAndHandlersPackage]
  );

  const panes = useMemo(
    () => [
      {
        menuItem: text('DepositPolicy'),
        render: depositsPane,
      },
      {
        menuItem: text('CancellationPolicy'),
        render: cancellationsPane,
      },
    ],
    [cancellationsPane, depositsPane]
  );

  return (
    <Modal
      size={isMobileOnly ? 'fullscreen' : 'tiny'}
      open={open}
      className={isMobileOnly ? 'mobile' : ''}
      style={{ zIndex: 1000000 }}
      {...rest}
    >
      {!isMobileOnly && (
        <Modal.Header>{text('ReservationPolicy')}</Modal.Header>
      )}
      <Modal.Content
        style={{ minHeight: 450, maxHeight: 450, overflowY: 'auto' }}
      >
        <Tab
          //   renderActiveOnly={false}
          menu={{ secondary: true, pointing: true }}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          panes={panes}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button size={'tiny'} negative onClick={close}>
          {text('Close')}
        </Button>
        <Button size={'tiny'} positive onClick={save} disabled={!hasChanges}>
          {text('Save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

CancellationAndDepositPoliciesPopup.propTypes = {
  onClose: PropTypes.func,
  startOpen: PropTypes.bool,
  reservationPolicy: PropTypes.object,
  policiesArray: PropTypes.array,
  setStateFunction: PropTypes.func,
};

export default CancellationAndDepositPoliciesPopup;
